import { z } from 'zod';

// --- Schemas ---

export const createUserSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  role: z.string().min(1, 'Le rôle est requis'),
  phone: z.string().optional(),
  assigned_country: z.string().optional(),
  country_role: z.string().optional(),
});

export const editUserSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  phone: z.string().optional(),
  role: z.string().min(1, 'Le rôle est requis'),
  assigned_country: z.string().optional(),
  country_role: z.string().optional(),
  is_active: z.boolean().optional(),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Le mot de passe actuel est requis'),
  newPassword: z.string().min(6, 'Le nouveau mot de passe doit contenir au moins 6 caractères'),
  confirmPassword: z.string().min(1, 'La confirmation est requise'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

// --- Création de colis : un schéma par étape du parcours ---

const optionalEmail = z.string().email('Email invalide').or(z.literal('')).optional();

const contactSchema = z.object({
  name: z.string().min(2, 'Le nom est requis'),
  phone: z.string().min(8, 'Le téléphone est requis'),
  email: optionalEmail,
});

// L'origine n'est saisie que par le partenaire ; côté client (QR code) elle est
// déduite du point de dépôt et le champ n'est pas affiché.
export const makeRouteSchema = (variant) =>
  z
    .object({
      transport_mode: z.string().min(1, 'Le mode de transport est requis'),
      origin_country: z.string().optional(),
      destination_country: z.string().min(1, 'Le pays de destination est requis'),
      destination_city: z.string().optional(),
    })
    .superRefine((d, ctx) => {
      if (variant === 'partner' && !d.origin_country) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['origin_country'],
          message: "Le pays d'origine est requis",
        });
      }
      if (d.origin_country && d.origin_country === d.destination_country) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['destination_country'],
          message: "Le pays d'origine et de destination ne peuvent pas être identiques",
        });
      }
    });

export const contactsSchema = z.object({
  sender: contactSchema,
  recipient: contactSchema,
});

export const makeItemsSchema = (transportMode) =>
  z.object({
    items: z
      .array(
        z.object({
          description: z.string().min(2, 'La description est requise'),
          weight: z.string().optional(),
          length_cm: z.string().optional(),
          width_cm: z.string().optional(),
          height_cm: z.string().optional(),
        }).superRefine((item, ctx) => {
          if (transportMode === 'maritime') return;
          if (!(parseFloat(item.weight) > 0)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['weight'],
              message: 'Le poids doit être supérieur à 0',
            });
          }
        })
      )
      .min(1, 'Au moins un colis est requis'),
  });

// Le paiement se décrit colis par colis : chacun porte son prix, son état et son
// moyen de règlement. Côté client (QR), le prix vient de la grille tarifaire et
// n'est donc pas saisi.
export const makePaymentSchema = (variant) =>
  z.object({
    items: z
      .array(
        z.object({
          price: z.string().optional(),
          payment_status: z.enum(['unpaid', 'paid', 'advance'], {
            errorMap: () => ({ message: 'Le statut de paiement est requis' }),
          }),
          advance_amount: z.string().optional(),
          payment_method: z.string().min(1, 'Le moyen de paiement est requis'),
        }).superRefine((item, ctx) => {
          const price = parseFloat(item.price);
          if (variant === 'partner' && !(price >= 0)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['price'],
              message: 'Le prix du colis est requis',
            });
          }
          if (item.payment_status !== 'advance') return;

          const advance = parseFloat(item.advance_amount);
          if (!(advance > 0)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['advance_amount'],
              message: "Le montant de l'avance est requis",
            });
            return;
          }
          if (price >= 0 && advance > price) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['advance_amount'],
              message: "L'avance ne peut pas être supérieure au prix du colis",
            });
          }
        })
      )
      .min(1, 'Au moins un colis est requis'),
  });

// --- Helper ---

export function validateForm(schema, data) {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data, errors: {} };
  }
  const errors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) {
      errors[key] = issue.message;
    }
  }
  return { success: false, errors };
}
